import React from 'react';
import { LockKeyholeIcon, MapPinIcon, RotateCcwIcon } from 'lucide-react';
import { RecoveryRules, defaultRecoveryRules, recoveryChannels, recoveryMarkets, rulesMatchDefaults } from '../data/recoveryRules';
type SubmissionRulesProps = {
  batchName: string;
  brand?: string;
  rules: RecoveryRules;
  // Only admins can lift a negotiated pricing lock.
  canUnlock?: boolean;
  // Preview mode: show the upload's rules without allowing edits.
  readOnly?: boolean;
  // Which rule groups to render. Onboarding shows them one at a time.
  sections?: Array<'channels' | 'pricing' | 'markets'>;
  // Onboarding explains the pricing limit differently than the live settings do.
  pricingHelper?: string;
  onChange: (rules: RecoveryRules) => void;
  onReset: () => void;
};
export function SubmissionRules({
  batchName,
  brand,
  rules,
  canUnlock = false,
  readOnly = false,
  sections = ['channels', 'pricing', 'markets'],
  pricingHelper,
  onChange,
  onReset
}: SubmissionRulesProps) {
  const shows = (section: 'channels' | 'pricing' | 'markets') => sections.includes(section);
  const twoColumn = shows('channels') && (shows('pricing') || shows('markets'));
  const inherited = rulesMatchDefaults(rules, brand, batchName) || readOnly;
  const toggleChannel = (id: string) => onChange({
    ...rules,
    channels: rules.channels.includes(id) ? rules.channels.filter(channel => channel !== id) : [...rules.channels, id]
  });
  const toggleMarket = (market: string) => onChange({
    ...rules,
    markets: rules.markets.includes(market) ? rules.markets.filter(item => item !== market) : [...rules.markets, market]
  });
  return <section className="rounded-2xl border border-[#BB5E40]/20 bg-cream/50 p-5" aria-label={`Recovery rules for ${batchName}`} data-id="element-2467" data-mp="2ng8lldmutxb5">
      {!inherited && <div className="flex justify-end" data-id="element-2468" data-mp="1ux2ysm2ffbur">
          <button type="button" onClick={onReset} className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold text-ink/65 transition-colors hover:bg-paper" data-id="element-2469" data-mp="1uvrwkqomqc5r">
            <RotateCcwIcon className="h-3 w-3" aria-hidden="true" data-id="element-2470" data-mp="3uzazyq7h2yp0" />
            Reset to defaults
          </button>
        </div>}

      <div className={`grid gap-6 ${twoColumn ? 'lg:grid-cols-[1.1fr_0.9fr]' : ''} ${inherited ? '' : 'mt-4'}`} data-id="element-2471" data-mp="3m85h9ql51y3">
        {shows('channels') && <div data-id="element-2472" data-mp="372exuk3jvcrk">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2473" data-mp="372ou2pf6ug5p">Approved channels</p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-2474" data-mp="3c0gkff06pu7s">
            {recoveryChannels.map(channel => {
            const active = rules.channels.includes(channel.id);
            return <button key={channel.id} type="button" role="switch" aria-checked={active} onClick={() => toggleChannel(channel.id)} disabled={readOnly} className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${active ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/15 bg-paper text-ink/60'} ${readOnly ? 'cursor-default' : active ? '' : 'hover:border-[#BB5E40]/40'}`} data-id="element-2475" data-mp="3l26j95webvfl">
                  {channel.iconUrl && <span aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 ${active ? 'bg-cream' : 'bg-ink/60'}`} style={{
                WebkitMaskImage: `url(${channel.iconUrl})`,
                maskImage: `url(${channel.iconUrl})`,
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain'
              }} data-id="element-2476" data-mp="rp4s1u1xg9k1" />}
                  {channel.name}
                </button>;
          })}
          </div>
          {rules.channels.length === 0 && <p className="mt-3 text-xs font-medium text-[#94452E]" data-id="element-2477" data-mp="266o3wtkcijfq">Select at least one channel or this upload cannot be activated.</p>}

        </div>}

        {(shows('pricing') || shows('markets')) && <div className="space-y-5" data-id="element-2478" data-mp="1wf1wz9f1j6is">
          {shows('pricing') && <RuleSlider label="Public pricing limit" detail={pricingHelper ?? 'The maximum discount from MSRP at which this upload can be publicly listed.'} value={rules.indexPrice} min={0} max={90} suffix="% OFF MSRP" locked={readOnly || rules.indexLocked} onLockChange={canUnlock && !readOnly ? indexLocked => onChange({
          ...rules,
          indexLocked
        }) : undefined} onChange={indexPrice => onChange({
          ...rules,
          indexPrice
        })} data-id="element-2479" data-mp="216tdss8e9xvq" />}
          {shows('markets') && <div data-id="element-2480" data-mp="yv1yzp6ze3y0">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2481" data-mp="ywmlcrj56gks">Market access</p>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-2482" data-mp="txs2xqp36u17">
              {recoveryMarkets.map(market => {
              const active = rules.markets.includes(market);
              return <button key={market} type="button" role="switch" aria-checked={active} onClick={() => toggleMarket(market)} disabled={readOnly} className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${active ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/15 bg-paper text-ink/60'} ${readOnly ? 'cursor-default' : active ? '' : 'hover:border-[#BB5E40]/40'}`} data-id="element-2483" data-mp="13vzic53q9413">
                    <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2484" data-mp="t7wlom81v7aw" />
                    {market}
                  </button>;
            })}
            </div>
            <p className="mt-2 text-xs text-ink/50" data-id="element-2485" data-mp="1282uhusszj3q">
              {rules.markets.length === 0 ? 'Select at least one market where this inventory can be sold.' : 'Regions where this upload can be marketed and sold.'}
            </p>
          </div>}
        </div>}
      </div>
    </section>;
}
function RuleSlider({
  label,
  detail,
  value,
  min,
  max,
  suffix,
  locked = false,
  onLockChange,
  onChange
}: {
  label: string;
  detail: string;
  value: number;
  min: number;
  max: number;
  suffix: string;
  locked?: boolean;
  onLockChange?: (locked: boolean) => void;
  onChange: (value: number) => void;
}) {
  const id = `rule-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const lockId = `${id}-lock`;
  return <div data-id="element-2486" data-mp="3rpipiuf4kmvr">
      <div className="flex items-baseline justify-between gap-3" data-id="element-2487" data-mp="3rpab2y3ct9ig">
        <label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2488" data-mp="3k6j7as3eh1dz">{label}</label>
        <span className={`text-sm font-semibold tabular-nums ${locked ? 'text-ink/55' : 'text-ink'}`} data-id="element-2489" data-mp="3fd7y4kb488iw">
          {value}
          <span className="ml-1 text-xs font-medium text-ink/50" data-id="element-2490" data-mp="3adllabz78uam">{suffix}</span>
        </span>
      </div>
      <input id={id} type="range" min={min} max={max} value={value} disabled={locked} onChange={event => onChange(Number(event.target.value))} className={`mt-2 w-full accent-[#BB5E40] ${locked ? 'cursor-not-allowed opacity-60' : ''}`} data-id="element-2491" data-mp="36c03qdbpzru3" />
      {onLockChange && <div className="mt-2 flex items-center gap-2" data-id="element-2492" data-mp="3p53xrzsuo6p8">
          <input id={lockId} type="checkbox" checked={locked} onChange={event => onLockChange(event.target.checked)} className="h-3.5 w-3.5 accent-[#BB5E40]" data-id="element-2493" data-mp="3p6ot3rmznto5" />
          <label htmlFor={lockId} className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-ink/65" data-id="element-2494" data-mp="hmiw9kolzwpl">
            <LockKeyholeIcon className={`h-3.5 w-3.5 ${locked ? 'text-[#BB5E40]' : 'text-ink/40'}`} aria-hidden="true" data-id="element-2495" data-mp="loxfw703r0hx" />
            Lock
          </label>
        </div>}
      <p className={`mt-1 text-xs ${locked ? 'font-medium text-[#94452E]' : 'text-ink/50'}`} data-id="element-2496" data-mp="cmxvd4pvpea8">
        {locked ? `Locked at the agreed ${value}% off MSRP.${onLockChange ? ' Unlock to adjust.' : ' Contact ManyCo to change your agreement.'}` : detail}
      </p>
    </div>;
}
export { defaultRecoveryRules };